import React from 'react';
import InputGroupButton from './InputGroupButton';

export interface InputGroupAddonProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Content of the addon (text, icon, etc.)
   */
  children?: React.ReactNode;
  /**
   * Optional inline style overrides
   */
  style?: React.CSSProperties;
}

/**
 * A simple InputGroup.Addon replacement for react-bootstrap's <InputGroup.Addon>.
 * Displays addon content (like icons) next to input fields.
 *
 * Replicates Bootstrap 3's .input-group-addon styling with inline styles.
 * Note: Border-radius adjustments are handled by parent InputGroup based on position.
 */
export const InputGroupAddon: React.FC<InputGroupAddonProps> = ({
  children,
  style = {},
  ...props
}) => {
  const baseStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
    fontSize: '14px',
    fontWeight: 'normal',
    lineHeight: 1,
    color: '#555',
    textAlign: 'center',
    backgroundColor: '#eee',
    border: '1px solid #ccc',
    borderRadius: 4,
    ...style,
  };

  return (
    <div style={baseStyle} {...props}>
      {children}
    </div>
  );
};

export interface InputGroupProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Children nodes (input, button, etc.)
   */
  children?: React.ReactNode;
  /**
   * Optional inline style overrides
   */
  style?: React.CSSProperties;
  /**
   * Size variant
   */
  bsSize?: 'small' | 'large';
}

/**
 * InputGroup replacement for react-bootstrap's <InputGroup>.
 * Wraps inputs and buttons, and applies horizontal alignment.
 *
 * Replicates Bootstrap 3's behavior where first/last children get adjusted border-radius
 * to create a seamless, connected appearance.
 */
const InputGroup: React.FC<InputGroupProps> & {
  Addon: typeof InputGroupAddon;
  Button: typeof InputGroupButton;
} = ({ style = {}, children, bsSize, className = '', ...props }) => {
  // Handle size variants
  const sizeClass = bsSize ? `input-group-${bsSize}` : '';
  const combinedClassName = ['input-group', sizeClass, className]
    .filter(Boolean)
    .join(' ');

  const baseStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    position: 'relative',
    width: '100%',
    ...style,
  };

  // Clone children and inject position-based border-radius styles
  // This replicates Bootstrap 3's CSS selector behavior:
  // .input-group :first-child { border-right-radius: 0 }
  // .input-group :last-child { border-left-radius: 0 }
  const childrenArray = React.Children.toArray(children);
  const enhancedChildren = childrenArray.map((child, index) => {
    if (!React.isValidElement(child)) return child;

    const isFirst = index === 0;
    const isLast = index === childrenArray.length - 1;

    // Build position-based border-radius and border overrides
    // This prevents doubled borders between adjacent elements
    const positionStyle: React.CSSProperties = {};
    if (isFirst) {
      positionStyle.borderTopRightRadius = 0;
      positionStyle.borderBottomRightRadius = 0;
    }
    if (isLast) {
      positionStyle.borderTopLeftRadius = 0;
      positionStyle.borderBottomLeftRadius = 0;
      positionStyle.borderLeft = 0; // Remove left border to prevent doubling with previous sibling
    }

    // Clone element and merge position styles into existing style prop
    // Safely extract existing style prop if it exists
    const childProps = child.props as { style?: React.CSSProperties };
    const existingStyle = childProps.style || {};

    return React.cloneElement(child, {
      style: { ...existingStyle, ...positionStyle },
    } as Partial<typeof child.props>);
  });

  // Note: Bootstrap uses <span>, but for flexbox to work properly we use <div>
  return (
    <>
      <style>
        {`
          .input-group .form-control-custom:first-child,
          .input-group .form-control-custom:first-child:focus {
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
          }
          .input-group .form-control-custom:last-child,
          .input-group .form-control-custom:last-child:focus {
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;
            border-left: 0 !important;
          }
        `}
      </style>
      <div style={baseStyle} className={combinedClassName} {...props}>
        {enhancedChildren}
      </div>
    </>
  );
};

// Static properties
InputGroup.Addon = InputGroupAddon;
InputGroup.Button = InputGroupButton;

export default InputGroup;
